import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { EditorState, LexicalEditor } from 'lexical';
import { CallToActionBrandProps } from 'modules/BrandDefinition';
import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import TextPlaceholder from 'components/ArtboardAssets/TextPlaceholder';
import { intlGet } from 'utils/intlGet';
import { lexicalCTAEditorConfig } from '../editors';
import { LexicalOnChangeCTA } from '../hooks/useLexicalCallToActionEditor';
import { AbbreviationPlugin } from '../Plugins/AbbreviationPlugin';
import { BasePropsPlugin } from '../Plugins/BasePropsPlugin';
import { CustomAutoFocusPlugin } from '../Plugins/CustomAutoFocusPlugin';
import { EditModeProviderPlugin } from '../Plugins/EditModeContextPlugin';
import { EditorRefPlugin } from '../Plugins/EditorRefPlugin';
import { EditorStateUpdatePlugin } from '../Plugins/EditorStateUpdatePlugin';
import { EditorStopEditingPlugin } from '../Plugins/EditorStopEditingPlugin';
import { FontPlugin } from '../Plugins/FontPlugin';
import { HistoryPluginChangeHandler, HistoryWithExtraStatePlugin } from '../Plugins/HistoryWithExtraStatePlugin';
import { PastePlugin } from '../Plugins/PastePlugin';
import { PlaceholderPlugin } from '../Plugins/PlaceholderPlugin';

type Props<T> = {
  editorState: string;
  editMode: boolean;
  onChange: LexicalOnChangeCTA;
  onStopEditing: (state: EditorState) => void;
  brandProps: CallToActionBrandProps;
  historyExtraState: T;
  historyExtraStateSetter: (value: T) => void;
  historyOnChange: HistoryPluginChangeHandler;
};

export const LexicalCallToActionEditor = forwardRef(function <T extends object>(props: Props<T>, ref: React.Ref<LexicalEditor>) {
  const {
    editorState,
    editMode,
    brandProps,
    onChange,
    onStopEditing,
    historyExtraState,
    historyExtraStateSetter,
    historyOnChange,
  } = props;

  const editorRef = useRef<LexicalEditor>(null);
  useImperativeHandle(ref, () => editorRef.current as LexicalEditor);

  return (
    <LexicalComposer initialConfig={{
      ...lexicalCTAEditorConfig,
      editable: editMode,
      editorState,
    }}>
      <EditModeProviderPlugin editMode={editMode}>
        <div className="editor-container">
          <RichTextPlugin
            contentEditable={<ContentEditable className="editor-input" data-testid="editor-input" />}
            ErrorBoundary={LexicalErrorBoundary}
          />
          {editMode && <HistoryWithExtraStatePlugin
            extraState={historyExtraState}
            extraStateSetter={historyExtraStateSetter}
            onChange={historyOnChange}
          />}
          <EditorRefPlugin ref={editorRef} />
          <BasePropsPlugin onChange={onChange.baseProps} />
          <FontPlugin
            onChange={onChange.font}
            colors={brandProps.colors}
            fonts={brandProps.fonts}
          />
          <AbbreviationPlugin onChange={onChange.abbreviations} />
          <CustomAutoFocusPlugin />
          <EditorStopEditingPlugin onStopEditing={onStopEditing} />
          <EditorStateUpdatePlugin editorState={editorState} />
          <PlaceholderPlugin placeholder={
            <TextPlaceholder text={intlGet('Artboard.CallToAction', 'Title')} />
          } />
          <PastePlugin />
        </div>
      </EditModeProviderPlugin>
    </LexicalComposer>
  );
});
